"use client"
import {
  FieldError,
  FieldValues,
  FormProvider,
  useForm,
  useFormContext,
} from "react-hook-form"
import { Button } from "@/components/atoms"
import { zodResolver } from "@hookform/resolvers/zod"
import { LabeledInput } from "@/components/cells"
import { registerFormSchema, RegisterFormData } from "./schema"
import { signup } from "@/lib/data/customer"
import { Suspense, useEffect, useState } from "react"
import { Container } from "@medusajs/ui"
import Link from "next/link"
import { useRouter, useSearchParams } from "next/navigation"
import { PasswordValidator } from "@/components/cells/PasswordValidator/PasswordValidator"
import { toast } from "@/lib/helpers/toast"
import Cookies from "js-cookie"

export const RegisterForm = () => {
  const methods = useForm<RegisterFormData>({
    resolver: zodResolver(registerFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
      password: "",
      referralCode: "",
    },
  })

  return (
    <Suspense fallback={<div className="flex justify-center py-24 text-gray-500">Loading registration form...</div>}>
      <FormProvider {...methods}>
        <Form />
      </FormProvider>
    </Suspense>
  )
}

const Form = () => {
  const router = useRouter()
  const searchParams = useSearchParams()
  const [isReadOnly, setIsReadOnly] = useState(false)
  const [passwordError, setPasswordError] = useState({
    isValid: false,
    lower: false,
    upper: false,
    "8chars": false,
    symbolOrDigit: false,
  })

  const {
    handleSubmit,
    register,
    watch,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = useFormContext<RegisterFormData>()

  useEffect(() => {
    const codeFromUrl = searchParams.get("ref")
    const codeFromCookie = Cookies.get("referrer_code")
    const code = codeFromUrl || codeFromCookie || ""
    if (code) {
      setValue("referralCode", code.trim())
      setIsReadOnly(true)
    } else {
      setIsReadOnly(false)
    }
  }, [searchParams, setValue])

  const submit = async (data: RegisterFormData) => {
    if (!passwordError.isValid) {
      console.warn("[RegisterForm submit] Password is not valid, aborting submission");
      return
    }

    console.log("[RegisterForm submit] Form submit triggered with data:", { ...data, password: "[REDACTED]" });

    try {
      const formData = new FormData()
      formData.append("email", data.email)
      formData.append("password", data.password)
      formData.append("first_name", data.firstName)
      formData.append("last_name", data.lastName)
      formData.append("phone", data.phone)
      formData.append("referral_code", data.referralCode)

      console.log("[RegisterForm submit] Invoking signup server action...");
      const res = await signup(formData)
      console.log("[RegisterForm submit] Signup response received:", res);

      if (res && (typeof res === "string" || !res?.id)) {
        // Temporary solution. Check also for status code when it's fixed by backend
        const errorStr = typeof res === "string" ? res : "Registration failed. Please try again."
        const errorMessage = errorStr.toLowerCase().includes('error: identity with email already exists')
          ? 'It seems the email you entered is already associated with another account. Please log in instead.'
          : errorStr
        console.warn("[RegisterForm submit] Registration failed:", errorMessage);

        // If the error message mentions email, show it on the email field
        if (errorMessage.toLowerCase().includes("email")) {
          setError("email", { type: "manual", message: errorMessage })
        } 
        // If it's about referral code or validation service error, show it on the referralCode field
        else if (errorMessage.toLowerCase().includes("referral code") || errorMessage.toLowerCase().includes("sponsor") || errorMessage.toLowerCase().includes("validation")) {
          setError("referralCode", { type: "manual", message: errorMessage })
        } else {
          toast.error({ title: errorMessage })
        }
      } else {
        console.log("[RegisterForm submit] Registration success! Redirecting...");
        router.push("/user/member-guide")
      }
    } catch (error: any) {
      console.error("[RegisterForm submit] Unexpected exception during submit:", error);
      toast.error({ title: error?.message || "An unexpected error occurred. Please try again." });
    }
  }

  return (
    <main className="container py-12" data-testid="register-page">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start max-w-7xl mx-auto px-4">
        {/* Left Side Content */}
        <div className="lg:col-span-6 space-y-8">
          <div className="space-y-6">
            <h1 className="font-serif text-4xl md:text-5xl font-normal text-gray-800 dark:text-gray-100 leading-tight">
              Become a Sparkanz Member
            </h1>
            <p className="text-gray-500 dark:text-gray-400 leading-relaxed text-base font-normal">
              Sparkanz was born from a single belief — that every person, in every corner of the world, deserves the opportunity to build a prosperous future through the power of global trade and human connection.
            </p>
          </div>

          {/* Inspirational Quotes (Smaller, side-by-side) */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="p-3 rounded-lg bg-gray-50/80 dark:bg-secondary/80 border-l-2 border-brand-500 flex flex-col justify-between">
              <p className="text-gray-500 dark:text-gray-400 italic text-[11px] leading-relaxed">&ldquo;A spark shared is a spark multiplied.&rdquo;</p>
              <div className="text-right text-[8px] font-semibold tracking-wider text-gray-400 dark:text-gray-500 mt-1">— Member Creed</div>
            </div>
            <div className="p-3 rounded-lg bg-gray-50/80 dark:bg-secondary/80 border-l-2 border-brand-500 flex flex-col justify-between">
              <p className="text-gray-500 dark:text-gray-400 italic text-[11px] leading-relaxed">&ldquo;The beauty of a spark — it costs nothing to share, yet changes everything.&rdquo;</p>
              <div className="text-right text-[8px] font-semibold tracking-wider text-gray-400 dark:text-gray-500 mt-1">— Trade Philosophy</div>
            </div>
            <div className="p-3 rounded-lg bg-gray-50/80 dark:bg-secondary/80 border-l-2 border-brand-500 flex flex-col justify-between">
              <p className="text-gray-500 dark:text-gray-400 italic text-[11px] leading-relaxed">&ldquo;Sparkanz: where one person&apos;s spark becomes a network&apos;s flame.&rdquo;</p>
              <div className="text-right text-[8px] font-semibold tracking-wider text-gray-400 dark:text-gray-500 mt-1">— Community Vision</div>
            </div>
          </div>

          {/* Membership Benefits (Moved here, layout adjusted for column) */}
          <div className="pt-8 border-t border-gray-200 dark:border-neutral-50/10">
            <h3 className="font-serif text-2xl font-normal text-gray-800 dark:text-gray-100 mb-6">
              Membership Benefits
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Benefit 1 */}
              <div className="p-4 rounded-xl bg-white dark:bg-secondary border border-gray-100 dark:border-neutral-50/10 shadow-sm flex gap-3">
                <div className="h-8 w-8 rounded-lg bg-brand-50 dark:bg-brand-600/10 text-brand-600 dark:text-brand-300 flex items-center justify-center border border-brand-100 dark:border-brand-600/20 shrink-0">
                  <svg className="w-4 h-4 text-brand-600 dark:text-brand-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-1">No inventory required</h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed font-normal">
                    Partners promote existing quality products and services with zero investment.
                  </p>
                </div>
              </div>

              {/* Benefit 2 */}
              <div className="p-4 rounded-xl bg-white dark:bg-secondary border border-gray-100 dark:border-neutral-50/10 shadow-sm flex gap-3">
                <div className="h-8 w-8 rounded-lg bg-brand-50 dark:bg-brand-600/10 text-brand-600 dark:text-brand-300 flex items-center justify-center border border-brand-100 dark:border-brand-600/20 shrink-0">
                  <svg className="w-4 h-4 text-brand-600 dark:text-brand-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-1">Truly global reach</h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed font-normal">
                    Active in 150+ countries with products suited to every major market and culture.
                  </p>
                </div>
              </div>

              {/* Benefit 3 */}
              <div className="p-4 rounded-xl bg-white dark:bg-secondary border border-gray-100 dark:border-neutral-50/10 shadow-sm flex gap-3">
                <div className="h-8 w-8 rounded-lg bg-brand-50 dark:bg-brand-600/10 text-brand-600 dark:text-brand-300 flex items-center justify-center border border-brand-100 dark:border-brand-600/20 shrink-0">
                  <svg className="w-4 h-4 text-brand-600 dark:text-brand-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 7.89M9 11l3 3L22 4" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-1">Residual income model</h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed font-normal">
                    Earn month after month on subscriptions and recurring services — not just one-time sales.
                  </p>
                </div>
              </div>

              {/* Benefit 4 */}
              <div className="p-4 rounded-xl bg-white dark:bg-secondary border border-gray-100 dark:border-neutral-50/10 shadow-sm flex gap-3">
                <div className="h-8 w-8 rounded-lg bg-brand-50 dark:bg-brand-600/10 text-brand-600 dark:text-brand-300 flex items-center justify-center border border-brand-100 dark:border-brand-600/20 shrink-0">
                  <svg className="w-4 h-4 text-brand-600 dark:text-brand-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-1">Regulated and compliant</h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed font-normal">
                    Operating with complete international regulatory compliance, ensuring long-term residual security.
                  </p>
                </div>
              </div>

              {/* Benefit 5 */}
              <div className="p-4 rounded-xl bg-white dark:bg-secondary border border-gray-100 dark:border-neutral-50/10 shadow-sm flex gap-3">
                <div className="h-8 w-8 rounded-lg bg-brand-50 dark:bg-brand-600/10 text-brand-600 dark:text-brand-300 flex items-center justify-center border border-brand-100 dark:border-brand-600/20 shrink-0">
                  <svg className="w-4 h-4 text-brand-600 dark:text-brand-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-1">World-class training</h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed font-normal">
                    Dedicated partner portal with courses, mentors, and live events in multiple languages.
                  </p>
                </div>
              </div>

              {/* Benefit 6 */}
              <div className="p-4 rounded-xl bg-white dark:bg-secondary border border-gray-100 dark:border-neutral-50/10 shadow-sm flex gap-3">
                <div className="h-8 w-8 rounded-lg bg-brand-50 dark:bg-brand-600/10 text-brand-600 dark:text-brand-300 flex items-center justify-center border border-brand-100 dark:border-brand-600/20 shrink-0">
                  <svg className="w-4 h-4 text-brand-600 dark:text-brand-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-1">People-first culture</h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed font-normal">
                    Every policy, product, and program is designed around partner success and community wellbeing.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side Register Form */}
        <div className="lg:col-span-6 space-y-8">
          <Container className="border dark:border-neutral-50/10 p-6 rounded-2xl bg-white dark:bg-secondary shadow-sm" data-testid="register-form-container">
            <h2 className="heading-md text-primary uppercase mb-8">
              Create account
            </h2>
            <form onSubmit={handleSubmit(submit)} data-testid="register-form">
              <div className="flex flex-col md:flex-row gap-4 mb-4">
                <LabeledInput
                  className="md:w-1/2"
                  label="First name"
                  placeholder="Your first name"
                  error={errors.firstName as FieldError}
                  data-testid="register-first-name-input"
                  {...register("firstName")}
                />
                <LabeledInput
                  className="md:w-1/2"
                  label="Last name"
                  placeholder="Your last name"
                  error={errors.lastName as FieldError}
                  data-testid="register-last-name-input"
                  {...register("lastName")}
                />
              </div>
              <div className="flex flex-col md:flex-row gap-4 mb-4">
                <LabeledInput
                  className="md:w-1/2"
                  label="E-mail"
                  placeholder="Your e-mail address"
                  error={errors.email as FieldError}
                  data-testid="register-email-input"
                  {...register("email")}
                />
                <LabeledInput
                  className="md:w-1/2"
                  label="Phone"
                  placeholder="Your phone number"
                  error={errors.phone as FieldError}
                  data-testid="register-phone-input"
                  {...register("phone")}
                />
              </div>
              <div>
                <LabeledInput
                  className="mb-4"
                  label="Password"
                  placeholder="Your password"
                  type="password"
                  error={errors.password as FieldError}
                  data-testid="register-password-input"
                  {...register("password")}
                />
                <PasswordValidator
                  password={watch("password")}
                  setError={setPasswordError}
                />
              </div>
              <div className="mt-4">
                <LabeledInput
                  className="mb-4"
                  label="Referral Code"
                  placeholder="Your sponsor's referral code"
                  error={errors.referralCode as FieldError}
                  readOnly={isReadOnly}
                  data-testid="register-referral-code-input"
                  {...register("referralCode")}
                />
              </div>

              <Button
                className="w-full flex justify-center mt-8 uppercase"
                disabled={isSubmitting}
                loading={isSubmitting}
                data-testid="register-submit-button"
              >
                Create account
              </Button>
            </form>
          </Container>

          <Container className="border dark:border-neutral-50/10 p-6 rounded-2xl bg-white dark:bg-secondary shadow-sm">
            <h2 className="heading-md text-primary uppercase mb-8">
              Already have an account?
            </h2>
            <Link href="/login" data-testid="register-login-link">
              <Button
                variant="tonal"
                className="w-full flex justify-center mt-8 uppercase"
              >
                Log in
              </Button>
            </Link>
          </Container>
        </div>
      </div>
    </main>
  )
}
